import { BaseRepository } from "@/repositories/base-repository"
import type { IWorkspaceNote } from "@/workspace/interfaces/api/workspace-note.interface"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import moment from "moment"
import type { WorkspaceRecurrence } from "@/workspace/interfaces/api/workspace-recurrence.interface"

function serializedPayload(data: Record<string, unknown>) {
  const rule = data.rule as WorkspaceRecurrence | undefined
  return {
    ...data,
    ...(rule
      ? {
          rule: {
            ...rule,
            ...(rule.until ? { until: moment(rule.until).format("YYYY-MM-DD") } : {})
          }
        }
      : {})
  }
}

function recurrenceDates(data: IWorkspaceNote): IWorkspaceNote {
  // The ordinary API uses local wall-clock strings. Recurrence IDs and dates
  // carry offsets so the two occurrences of an autumn clock hour stay distinct.
  for (const key of [
    "occurrenceAt",
    "recurrenceStart",
    "remindAt",
    "completedAt",
    "deletedAt"
  ] as const) {
    if (data[key]) data[key] = moment(data[key])
  }
  for (const item of data.checklistItems ?? []) {
    if (item.remindAt) item.remindAt = moment(item.remindAt)
    if (item.completedAt) item.completedAt = moment(item.completedAt)
  }
  return data
}

export class WorkspaceNoteRepository extends BaseRepository<IWorkspaceNote> {
  protected resourcePath = "workspace/notes"

  async createSeries(data: Record<string, unknown>): Promise<IWorkspaceNote> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      "workspace/notes/create-series/"
    )
      .setData(serializedPayload(data))
      .setExpectedStatusCode(201)
      .build()
      .execute()
    return response.data as IWorkspaceNote
  }

  async occurrences(query: Record<string, string | number>) {
    const response = await new HTTPRequestBuilder(HTTPMethods.GET, "workspace/notes/occurrences/")
      .query(query)
      .build()
      .execute()
    const page = response.data as {
      results: IWorkspaceNote[]
      next: string | null
      nextPeriod: import("moment").Moment | null
    }
    page.results = page.results.map(recurrenceDates)
    page.nextPeriod = page.nextPeriod ? moment(page.nextPeriod) : null
    return page
  }

  async series(id: string, data: Record<string, unknown>): Promise<IWorkspaceNote> {
    const response = await new HTTPRequestBuilder(HTTPMethods.POST, `workspace/notes/${id}/series/`)
      .setData(serializedPayload(data))
      .build()
      .execute()
    return recurrenceDates(response.data as IWorkspaceNote)
  }
}
